/**
 * Translation shield — make the React commit phase survive DOM
 * mutations performed by browser translators.
 *
 * Why this exists
 * ───────────────────────────────────────────────────────────────
 * Chrome Translate, Safari Translate, Firefox's built-in translator,
 * and extensions (ImTranslator, the stand-alone Google Translate
 * extension, etc.) all work the same way: they walk the live DOM,
 * **replace text nodes** with translated ones, and sometimes wrap
 * them in `<font>` elements. React's fiber tree still holds pointers
 * to the ORIGINAL text nodes. When the next commit wants to insert a
 * new element next to one of those (e.g. a loading spinner appearing
 * beside a label) or remove it, React calls:
 *
 *   parent.insertBefore(newNode, referenceNode)
 *   parent.removeChild(referenceNode)
 *
 * and gets:
 *
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node': The
 *   node before which the new node is to be inserted is not a child
 *   of this node.
 *
 * This bubbles up to our PageErrorBoundary and the user sees the
 * "Algo deu errado" crash screen.
 *
 * We absolutely need browser translation to keep working — our
 * `@tonnie/locale` catalogue today ships `en` + `pt-PT`, so speakers
 * of pt-BR, es, de, fr, it, nl, … depend on their browser to read
 * the app. Blocking translation via `<meta notranslate>` is not an
 * option.
 *
 * The shield
 * ───────────────────────────────────────────────────────────────
 * Patch `Node.prototype.insertBefore` and `Node.prototype.removeChild`
 * so that cross-parent calls no-op (for `removeChild`) or degrade to
 * `appendChild` (for `insertBefore`) instead of throwing. React's
 * next reconciliation pass re-diffs against whatever the current DOM
 * actually contains, and the translator's next MutationObserver tick
 * re-scans and translates the newly-inserted content. The UX is a
 * tiny flicker at worst; the app does NOT crash.
 *
 * This is the same patch endorsed by the React team in
 * https://github.com/facebook/react/issues/11538 and has been running
 * in production on facebook.com since ~2018. It is safe and stable.
 *
 * Install site
 * ───────────────────────────────────────────────────────────────
 * Must run BEFORE any React code creates a root. Import this module
 * as the very first line of `main.tsx` — the side-effect installs
 * the patch; no exported identifier is needed.
 *
 *     import './shared/translation-shield';
 *
 * The patch is idempotent (guarded by a globalThis flag) so HMR and
 * test environments that share a realm won't double-install.
 */

// We stash the install marker on `globalThis` (works in browser,
// worker, jsdom) but keep TS happy without a `declare global` that
// would pollute other modules' types.
interface GlobalShieldMarker {
  __tonnieTranslationShieldInstalled?: boolean;
}
function globalMarker(): GlobalShieldMarker {
  return globalThis as unknown as GlobalShieldMarker;
}

function install(): void {
  if (typeof globalThis === 'undefined') return;
  if (globalMarker().__tonnieTranslationShieldInstalled) return;
  if (typeof Node === 'undefined' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  // We preserve the original call signature and return type by using
  // the same overload shape the DOM declares — `removeChild<T extends Node>(child: T): T`.
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child && child.parentNode !== this) {
      // The child we were asked to remove isn't ours anymore — almost
      // always because a browser translator replaced it. Returning the
      // child without touching the DOM satisfies React (which just
      // wanted the reference back) and avoids the NotFoundError.
      //
      // We log at debug level so the mutation is visible in DevTools
      // for anyone investigating, but don't spam the console — this
      // can fire many times per page when Google Translate is active.
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug(
          '[translation-shield] removeChild skipped: child is not in this parent anymore (likely browser translator)',
          { child, parent: this }
        );
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node is no longer a child of `this` (translator moved
      // or replaced it). Degrade to `append`: React's next render pass
      // will re-diff against the real DOM and fix any ordering. The
      // translator re-scans soon after and translates the new node too.
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug(
          '[translation-shield] insertBefore degraded to append: reference not a child (likely browser translator)',
          { newNode, referenceNode, parent: this }
        );
      }
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  globalMarker().__tonnieTranslationShieldInstalled = true;
}

install();
